import { getAttributeModifier } from '@hexcrawl/shared-utils';
import type { AbilityDTO, IAttributes } from '@hexcrawl/shared-utils';

// Damage never drops below this no matter how poor the caster's modifier.
const MIN_DISPLAYED_DAMAGE = 1;
// What an attribute score means when the character has no value for it.
const DEFAULT_ATTRIBUTE_SCORE = 10;

/**
 * The numbers an ability button shows: its damage band as this character would roll it,
 * and the skill that backs it. Shared by the ability bar and the Use Item popover so a
 * scroll advertises exactly what casting the spell directly would.
 */
export interface AbilityEffectSummary {
  minDamage: number | null;
  maxDamage: number | null;
  casterSkill: string | null;
  skillRank: number | null;
}

export function summarizeAbilityEffect(
  ability: AbilityDTO,
  skills: Record<string, number | undefined> | undefined,
  attributes: IAttributes | undefined,
): AbilityEffectSummary {
  const args = ability.script?.args ?? {};

  const casterSkill = args.casterSkill ? String(args.casterSkill) : null;
  const skillRank = casterSkill != null ? (skills?.[casterSkill] ?? 0) : null;

  const damageModKey = args.damageMod ? String(args.damageMod) as keyof IAttributes : null;
  const damageMod = damageModKey && attributes
    ? getAttributeModifier(attributes[damageModKey] ?? DEFAULT_ATTRIBUTE_SCORE)
    : 0;

  const minBase = args.minDamage != null ? Number(args.minDamage) : null;
  const maxBase = args.maxDamage != null ? Number(args.maxDamage) : null;

  return {
    minDamage: minBase != null ? Math.max(MIN_DISPLAYED_DAMAGE, minBase + damageMod) : null,
    maxDamage: maxBase != null ? Math.max(MIN_DISPLAYED_DAMAGE, maxBase + damageMod) : null,
    casterSkill,
    skillRank,
  };
}
